import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import { Settings } from "../../../settings";
import { formatToCustomLocale } from "../../../utilities";

// Win text configuration - fine tune both global and per-line win text
export const WIN_TEXT_CONFIG = {
  // Font size as multiplier of SYMBOL_HEIGHT (higher = bigger text)
  GLOBAL_WIN_FONT_SIZE: 0.4, // Font size for "show all wins" phase
  LINE_WIN_FONT_SIZE: 0.35, // Font size for individual line cycling

  // Spine scale offset (applied on top of auto-calculated symbol scale)
  SPINE_SCALE_OFFSET: 0, // Fine tune: positive = bigger spine, negative = smaller

  // Text Y offset relative to bone position (positive = down, negative = up)
  TEXT_Y_OFFSET: 0,
};

/**
 * WinTextController - Unified controller for both global and line win text
 * Uses winline_digit spine with txt_winline_value bone for text attachment
 * Configurable font size allows it to serve both purposes
 */
class WinTextController {
  constructor(container, symbolWidth, symbolHeight, fontSizeMultiplier = 0.25) {
    this.container = container;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.fontSizeMultiplier = fontSizeMultiplier; // 0.25 for line wins, 0.35 for global wins

    // Spine instance
    this.winlineSpine = null;
    this.initialized = false;
    this.isCleanedUp = false;

    // Text container attached to spine bone
    this.textContainer = null;
    this.textBitmap = null;
    this.boneUpdateTicker = null;

    // Internal cache for performance optimization
    this._scaleCache = null;
    this._lastSymbolWidth = symbolWidth;
    this._lastSymbolHeight = symbolHeight;

    this.initializeSpine();
  }

  /**
   * Check if controller is ready
   */
  isReady() {
    return this.winlineSpine?.state && this.initialized && !this.isCleanedUp;
  }

  /**
   * Get the spine instance for event listening
   */
  getSpineInstance() {
    return this.winlineSpine;
  }

  /**
   * Initialize the winline_digit spine
   */
  initializeSpine() {
    try {
      const assets = PIXI.Assets.cache;
      if (
        !assets.has("winline_digit-skeleton") ||
        !assets.has("winline_digit-atlas")
      ) {
        return;
      }

      const skeletonData = PIXI.Assets.get("winline_digit-skeleton");
      const atlasData = PIXI.Assets.get("winline_digit-atlas");

      if (!skeletonData || !atlasData) {
        return;
      }

      this.winlineSpine = Spine.from({
        skeleton: "winline_digit-skeleton",
        atlas: "winline_digit-atlas",
      });

      if (
        !this.winlineSpine ||
        !this.winlineSpine.skeleton ||
        !this.winlineSpine.state
      ) {
        return;
      }

      // Scale spine to match symbol size
      this.applySpineScaling();

      // Position at center
      this.winlineSpine.x = 0;
      this.winlineSpine.y = 0;
      this.winlineSpine.zIndex = 20;
      this.winlineSpine.alpha = 0.001; // Invisible but stays in GPU pipeline

      this.container.addChild(this.winlineSpine);

      // Initialize text attached to bone
      this.initializeText();

      this.initialized = true;

      // Warm up spine to prevent first-use jitter
      this.warmUpSpine();
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Hide smoke effect by removing attachments from smoke-related slots
   * NOTE: Disabled — full spine animation now plays including all effects
   */
  hideSmokeEffect() {
    // No-op: full animation enabled
  }

  /**
   * Warm up spine by setting animation and updating transforms
   * This ensures spine is fully ready before first use (prevents jitter)
   */
  warmUpSpine() {
    if (!this.winlineSpine?.state) return;

    try {
      // Play animation briefly to populate spine structure and GPU buffers
      this.winlineSpine.state.setAnimation(0, "winline_digit", false);
      this.winlineSpine.state.update(0.016);

      // Populate bone hierarchy for GPU
      try {
        this.winlineSpine.skeleton.updateWorldTransform();
      } catch (e) {
        // Physics not initialized — safe to continue
      }

      // Clear tracks and reset to setup pose for clean first use
      this.winlineSpine.state.clearTracks();
      this.winlineSpine.state.clearListeners();
      this.winlineSpine.skeleton.setToSetupPose();
    } catch (error) {
      // Silent fail - warmup is optional optimization
    }
  }

  /**
   * Apply scaling to match symbol size
   * Cached for performance - invalidated when dimensions change
   */
  applySpineScaling() {
    if (!this.winlineSpine) return;

    // Check cache first
    if (
      this._scaleCache !== null &&
      this._lastSymbolWidth === this.symbolWidth &&
      this._lastSymbolHeight === this.symbolHeight
    ) {
      this.winlineSpine.scale.set(this._scaleCache);
      return;
    }

    // Scale to match symbol size (same pattern as OverlayReelController)
    const nativeWidth = Settings.SYMBOL_WIDTH;
    const nativeHeight = Settings.SYMBOL_HEIGHT;
    const widthScale = this.symbolWidth / nativeWidth;
    const heightScale = this.symbolHeight / nativeHeight;
    const scaleRatio = Math.min(widthScale, heightScale);

    // Cache result
    this._scaleCache = scaleRatio;
    this._lastSymbolWidth = this.symbolWidth;
    this._lastSymbolHeight = this.symbolHeight;

    this.winlineSpine.scale.set(scaleRatio);
  }

  /**
   * Initialize text container and attach to spine bone
   */
  initializeText() {
    if (!this.winlineSpine || !this.winlineSpine.skeleton) {
      return;
    }

    // Verify bone exists
    const textBone = this.winlineSpine.skeleton.findBone("txt");
    if (!textBone) {
      return;
    }

    // Create text container as child of spine
    this.textContainer = new PIXI.Container();
    this.textContainer.name = "winline_text_attached_to_bone";
    this.textContainer.alpha = 0;
    this.textContainer.zIndex = 1000;

    this.winlineSpine.addChild(this.textContainer);
    this.winlineSpine.sortableChildren = true;

    // Create BitmapText once during initialization
    // Use configurable font size multiplier
    // Text scales automatically because it's a child of the spine
    const fontSize = Math.floor(
      Settings.SYMBOL_HEIGHT * this.fontSizeMultiplier,
    );

    this.textBitmap = new PIXI.BitmapText("", {
      fontName: "font",
      fontSize: fontSize,
    });

    this.textBitmap.anchor.set(0.5);
    this.textBitmap.x = 0;
    this.textBitmap.y = 0;
    this.textBitmap.alpha = 0.001;

    this.textContainer.addChild(this.textBitmap);

    // Warm up BitmapText by rendering a realistic value to generate glyphs
    // This ensures first showWinAmount() call is smooth
    this.textBitmap.text = "1,234.56";
    this.textBitmap.updateText();
    this.textBitmap.dirty = true;

    // Clear warmup text after glyph generation to prevent stale text flash
    this.textBitmap.text = "";
    this.textBitmap.updateText();
  }

  /**
   * Update text display with new value
   */
  updateTextDisplay(value) {
    if (!this.textBitmap) return;

    // Format value - numbers only, no currency symbol
    const formattedValue = formatToCustomLocale(Math.floor(value));

    // Update the text value
    this.textBitmap.text = formattedValue;

    // Force synchronous update - ensures text geometry is fully regenerated
    // before the spine becomes visible (prevents old text flash)
    this.textBitmap.updateText();
    this.textBitmap.dirty = true;
  }

  /**
   * Sync text container transform with spine's txt_winline_value bone
   * Called every frame while spine is animating
   */
  updateTextFromBone() {
    if (
      !this.winlineSpine ||
      !this.textContainer ||
      !this.winlineSpine.skeleton
    ) {
      return;
    }

    const textBone = this.winlineSpine.skeleton.findBone("txt");
    if (!textBone) {
      return;
    }

    // Apply bone's world transform to text container (position, rotation, scale)
    this.textContainer.x = textBone.worldX;
    this.textContainer.y = textBone.worldY;
    this.textContainer.rotation =
      (textBone.getWorldRotationX() * Math.PI) / 180;
    this.textContainer.scale.set(
      textBone.getWorldScaleX(),
      textBone.getWorldScaleY(),
    );

    // Apply slot's color alpha to text container (for fadeout animation)
    const textSlot = this.winlineSpine.skeleton.findSlot("txt_slot_value");
    if (textSlot && textSlot.color) {
      this.textContainer.alpha = textSlot.color.a;
    }
  }

  /**
   * Start syncing text with spine bone every frame
   */
  startBoneUpdateLoop() {
    if (!this.winlineSpine || !this.textContainer) return;

    if (!this.boneUpdateTicker) {
      this.boneUpdateTicker = () => {
        if (this.winlineSpine && this.winlineSpine.alpha > 0.01) {
          this.updateTextFromBone();
        }
      };
      PIXI.Ticker.shared.add(this.boneUpdateTicker);
    }
  }

  /**
   * Stop syncing text with spine bone
   */
  stopBoneUpdateLoop() {
    if (this.boneUpdateTicker) {
      PIXI.Ticker.shared.remove(this.boneUpdateTicker);
      this.boneUpdateTicker = null;
    }
  }

  /**
   * Show win amount with spine animation
   * Animation will loop - caller is responsible for hiding when appropriate
   */
  showWinAmount(amount) {
    if (!this.isReady()) return;

    // Step 1: Ensure invisible during setup (alpha-only, no visible toggle)
    this.winlineSpine.alpha = 0.001;

    // Step 2: Update text while invisible
    this.updateTextDisplay(amount);

    // Step 3: Reset spine animation state
    // NOTE: Do NOT call setToSetupPose() — causes DarkSlotMesh memory leak in spine-pixi-v7
    if (this.winlineSpine.state) {
      this.winlineSpine.state.clearTracks();
      this.winlineSpine.state.clearListeners();
    }

    // Step 4: Set animation
    if (this.winlineSpine.state) {
      this.winlineSpine.state.setAnimation(0, "winline_digit", false);
      this.winlineSpine.state.apply(this.winlineSpine.skeleton);
      this.winlineSpine.state.update(0.016);
      this.winlineSpine.state.apply(this.winlineSpine.skeleton);
    }

    // Step 5: Start bone update loop (will run on next ticker frame)
    this.startBoneUpdateLoop();

    // Step 6: Reveal on NEXT frame after ticker has updated world transforms
    if (this._showRAF) cancelAnimationFrame(this._showRAF);
    this._showRAF = requestAnimationFrame(() => {
      this._showRAF = null;
      if (!this.winlineSpine || this.winlineSpine.destroyed) return;

      // Sync text with bone AFTER ticker has updated world transforms
      this.updateTextFromBone();

      // Now reveal (alpha-only, spine stays in GPU pipeline)
      this.winlineSpine.alpha = 1;
      this.textContainer.alpha = 1;
      if (this.textBitmap) {
        this.textBitmap.alpha = 1;
      }
    });
  }

  /**
   * Hide the win text - forceful cleanup to prevent stuck animations
   */
  hide() {
    if (!this.winlineSpine) return;

    // Cancel any pending show
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }

    // Stop bone update loop
    this.stopBoneUpdateLoop();

    // Hide via alpha (stays in GPU pipeline)
    this.winlineSpine.alpha = 0.001;

    // Clear text
    if (this.textBitmap) {
      this.textBitmap.text = "";
      this.textBitmap.alpha = 0.001;
    }
    if (this.textContainer) {
      this.textContainer.alpha = 0.001;
    }

    // Clean spine state
    // NOTE: Do NOT call setToSetupPose() — causes DarkSlotMesh memory leak in spine-pixi-v7
    if (this.winlineSpine.state) {
      this.winlineSpine.state.clearTracks();
      this.winlineSpine.state.clearListeners();
    }
  }

  /**
   * Update dimensions when symbol size changes
   */
  updateDimensions(newSymbolWidth, newSymbolHeight) {
    if (
      this.symbolWidth === newSymbolWidth &&
      this.symbolHeight === newSymbolHeight
    ) {
      return;
    }

    this.symbolWidth = newSymbolWidth;
    this.symbolHeight = newSymbolHeight;

    // Invalidate cache when dimensions change
    this._scaleCache = null;

    // Only need to scale the spine - BitmapText scales automatically as a child of the spine
    // Font size is based on Settings.SYMBOL_HEIGHT (native), so it doesn't need to change
    if (this.winlineSpine) {
      this.applySpineScaling();
    }
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    this.isCleanedUp = true;

    // Cancel any pending show rAF
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }

    // Stop bone update loop
    this.stopBoneUpdateLoop();

    // Cleanup text
    if (this.textBitmap) {
      this.textBitmap.destroy();
      this.textBitmap = null;
    }

    if (this.textContainer) {
      if (this.textContainer.parent) {
        this.textContainer.parent.removeChild(this.textContainer);
      }
      this.textContainer.destroy({ children: true });
      this.textContainer = null;
    }

    // Cleanup spine
    if (this.winlineSpine && !this.winlineSpine.destroyed) {
      try {
        if (this.winlineSpine.state) {
          this.winlineSpine.state.clearTracks();
          this.winlineSpine.state.clearListeners();
        }
        if (this.winlineSpine.parent) {
          this.winlineSpine.parent.removeChild(this.winlineSpine);
        }
        this.winlineSpine.destroy({
          children: true,
          texture: false,
          baseTexture: false,
        });
      } catch (error) {
        // Silent fail
      }
    }

    this.winlineSpine = null;
    this.container = null;
    this.initialized = false;
  }
}

export default WinTextController;
